import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { format, startOfDay, endOfDay, subDays, subWeeks, subMonths, subYears } from 'date-fns';
import { FILTER_PERIODS } from '../../constants';
import './CustomDateRangePicker.css';

/**
 * Custom Date Range Picker Component
 * Allows flexible date selection with preset options and custom date ranges
 * @param {Object} props - Component props
 * @param {string|Object} props.period - Currently selected period (string or {start, end})
 * @param {Function} props.onPeriodChange - Period change handler
 * @param {string} [props.className] - Additional CSS classes
 */
const CustomDateRangePicker = ({ period, onPeriodChange, className = '', persistToStorage = true, storageKey = 'admin-date-range' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [error, setError] = useState(null);
  const pickerRef = useRef(null);

  // Load persisted date range from localStorage
  useEffect(() => {
    if (persistToStorage) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.start && parsed.end) {
            const savedStart = new Date(parsed.start);
            const savedEnd = new Date(parsed.end);
            // Only use saved range if it's valid and not too old (e.g., within last 30 days)
            if (!isNaN(savedStart.getTime()) && !isNaN(savedEnd.getTime())) {
              setStartDate(savedStart);
              setEndDate(savedEnd);
              setSelectedPreset(null);
              // Notify parent if period prop is not provided
              if (!period) {
                onPeriodChange({ start: savedStart, end: savedEnd });
              }
            }
          }
        }
      } catch (error) {
        console.warn('Failed to load persisted date range:', error);
      }
    }
  }, []); // Only run on mount

  // Initialize dates from period prop
  useEffect(() => {
    if (typeof period === 'object' && period.start && period.end) {
      const start = new Date(period.start);
      const end = new Date(period.end);
      
      // Validate dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        setError('Invalid date range');
        return;
      }
      
      if (start > end) {
        setError('Start date must be before end date');
        return;
      }
      
      setStartDate(start);
      setEndDate(end);
      setSelectedPreset(null);
      setError(null);
      
      // Persist to localStorage
      if (persistToStorage) {
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            start: start.toISOString(),
            end: end.toISOString()
          }));
        } catch (error) {
          console.warn('Failed to persist date range:', error);
        }
      }
    } else if (typeof period === 'string') {
      // Convert preset period to date range
      const now = new Date();
      let start = null;
      let end = now;

      switch (period) {
        case FILTER_PERIODS.DAY:
          start = startOfDay(now);
          end = endOfDay(now);
          setSelectedPreset(FILTER_PERIODS.DAY);
          break;
        case FILTER_PERIODS.WEEK:
          start = startOfDay(subDays(now, 7));
          end = endOfDay(now);
          setSelectedPreset(FILTER_PERIODS.WEEK);
          break;
        case FILTER_PERIODS.MONTH:
          start = startOfDay(subMonths(now, 1));
          end = endOfDay(now);
          setSelectedPreset(FILTER_PERIODS.MONTH);
          break;
        case FILTER_PERIODS.YEAR:
          start = startOfDay(subYears(now, 1));
          end = endOfDay(now);
          setSelectedPreset(FILTER_PERIODS.YEAR);
          break;
        case FILTER_PERIODS.ALL:
          start = null; // All time
          end = now;
          setSelectedPreset(FILTER_PERIODS.ALL);
          break;
        default:
          setSelectedPreset(null);
      }

      setStartDate(start);
      setEndDate(end);
      setError(null);
    }
  }, [period, persistToStorage, storageKey]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const presets = [
    { value: FILTER_PERIODS.DAY, label: 'Today', getDates: () => ({ start: startOfDay(new Date()), end: endOfDay(new Date()) }) },
    { value: FILTER_PERIODS.WEEK, label: 'Last 7 Days', getDates: () => ({ start: startOfDay(subDays(new Date(), 7)), end: endOfDay(new Date()) }) },
    { value: FILTER_PERIODS.MONTH, label: 'Last 30 Days', getDates: () => ({ start: startOfDay(subDays(new Date(), 30)), end: endOfDay(new Date()) }) },
    { value: FILTER_PERIODS.MONTH, label: 'This Month', getDates: () => ({ start: startOfDay(subMonths(new Date(), 1)), end: endOfDay(new Date()) }) },
    { value: FILTER_PERIODS.YEAR, label: 'Last 90 Days', getDates: () => ({ start: startOfDay(subDays(new Date(), 90)), end: endOfDay(new Date()) }) },
    { value: FILTER_PERIODS.YEAR, label: 'This Year', getDates: () => ({ start: startOfDay(subYears(new Date(), 1)), end: endOfDay(new Date()) }) },
    { value: FILTER_PERIODS.ALL, label: 'All Time', getDates: () => ({ start: null, end: new Date() }) }
  ];

  const handlePresetSelect = (preset) => {
    try {
      const dates = preset.getDates();
      
      // Validate dates
      if (dates.start && dates.end && dates.start > dates.end) {
        setError('Invalid date range');
        return;
      }
      
      setStartDate(dates.start);
      setEndDate(dates.end);
      setSelectedPreset(preset.value);
      setError(null);
      
      // Persist to localStorage
      if (persistToStorage && dates.start && dates.end) {
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            start: dates.start.toISOString(),
            end: dates.end.toISOString(),
            preset: preset.value
          }));
        } catch (error) {
          console.warn('Failed to persist date range:', error);
        }
      }
      
      onPeriodChange(dates);
      setIsOpen(false);
    } catch (error) {
      setError('Failed to set date range');
      console.error('Error selecting preset:', error);
    }
  };

  const validateDateRange = (start, end) => {
    if (!start || !end) {
      return { valid: false, error: 'Both start and end dates are required' };
    }
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { valid: false, error: 'Invalid date format' };
    }
    
    if (start > end) {
      return { valid: false, error: 'Start date must be before end date' };
    }
    
    const maxRange = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
    if (end - start > maxRange) {
      return { valid: false, error: 'Date range cannot exceed 1 year' };
    }
    
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 1);
    if (end > futureDate) {
      return { valid: false, error: 'End date cannot be in the future' };
    }
    
    return { valid: true, error: null };
  };

  const handleCustomDateChange = () => {
    if (startDate && endDate) {
      const validation = validateDateRange(startDate, endDate);
      
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      
      setError(null);
      setSelectedPreset(null);
      
      // Persist to localStorage
      if (persistToStorage) {
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            start: startDate.toISOString(),
            end: endDate.toISOString()
          }));
        } catch (error) {
          console.warn('Failed to persist date range:', error);
        }
      }
      
      onPeriodChange({ start: startDate, end: endDate });
    } else {
      setError('Please select both start and end dates');
    }
  };

  const handleStartDateChange = (e) => {
    const date = e.target.value ? new Date(e.target.value) : null;
    setStartDate(date);
    setError(null);
    
    if (date && endDate) {
      const validation = validateDateRange(date, endDate);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      
      setSelectedPreset(null);
      
      // Persist to localStorage
      if (persistToStorage) {
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            start: date.toISOString(),
            end: endDate.toISOString()
          }));
        } catch (error) {
          console.warn('Failed to persist date range:', error);
        }
      }
      
      onPeriodChange({ start: date, end: endDate });
    }
  };

  const handleEndDateChange = (e) => {
    const date = e.target.value ? new Date(e.target.value) : null;
    setEndDate(date);
    setError(null);
    
    if (startDate && date) {
      const validation = validateDateRange(startDate, date);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      
      setSelectedPreset(null);
      
      // Persist to localStorage
      if (persistToStorage) {
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            start: startDate.toISOString(),
            end: date.toISOString()
          }));
        } catch (error) {
          console.warn('Failed to persist date range:', error);
        }
      }
      
      onPeriodChange({ start: startDate, end: date });
    }
  };

  const formatDateRange = () => {
    if (selectedPreset) {
      const preset = presets.find(p => p.value === selectedPreset);
      return preset ? preset.label : 'Custom Range';
    }
    
    if (startDate && endDate) {
      return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
    }
    
    return 'Select Date Range';
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  return (
    <div className={`custom-date-range-picker ${className}`} ref={pickerRef}>
      <label className="filter-label">Date Range</label>
      <button
        className="date-range-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <i className="bi bi-calendar3 icon-brand"></i>
        <span>{formatDateRange()}</span>
        <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </button>

      {isOpen && (
        <div className="date-range-dropdown">
          <div className="date-range-presets">
            <h5>Quick Select</h5>
            {presets.map((preset, index) => (
              <button
                key={index}
                className={`preset-button ${selectedPreset === preset.value ? 'active' : ''}`}
                onClick={() => handlePresetSelect(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="date-range-custom">
            <h5>Custom Range</h5>
            <div className="custom-date-inputs">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={formatDateForInput(startDate)}
                  onChange={handleStartDateChange}
                  max={formatDateForInput(endDate || new Date())}
                />
              </div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={formatDateForInput(endDate)}
                  onChange={handleEndDateChange}
                  min={formatDateForInput(startDate)}
                  max={formatDateForInput(new Date())}
                />
              </div>
            </div>
            {error && (
              <div className="date-range-error" role="alert">
                <i className="bi bi-exclamation-triangle"></i>
                {error}
              </div>
            )}
            <button
              className="apply-button"
              onClick={() => {
                handleCustomDateChange();
                if (!error) {
                  setIsOpen(false);
                }
              }}
              disabled={!startDate || !endDate || !!error}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

CustomDateRangePicker.propTypes = {
  period: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    })
  ]),
  onPeriodChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  persistToStorage: PropTypes.bool,
  storageKey: PropTypes.string
};

export default CustomDateRangePicker;

